<template>
  <div class="card adviser-schedule-edit-page">
    <div class="card-body">
      <div class="mb-3">
        <ActiveAdviserToggle
          :adviser="adviser"
          :is-editable="isEditable"
          :uneditable-message="uneditableMessage"
        />
      </div>

      <hr class="my-4" />
      <div v-if="showMondayPicker">
        <MondayPicker :date="scheduleCycleStartsOn" :adviser-id="adviser.teamMemberId" />
      </div>

      <div
        v-if="!adviser.isActive"
        :class="['alert', 'alert-outline', 'alert-warning']"
        role="alert"
      >
        {{ $t('models/conciergeAdviserSchedulePeriods.active_status.notification') }}
      </div>
      <div class="d-block mb-2">
        <template v-if="isEditable">
          <div class="mt-2">
            <div v-if="showMultiplePeriodForm" class="d-block mb-3">
              <ul class="nav nav-tabs">
                <li
                  v-for="period in schedulePeriods"
                  :key="period.id"
                  class="nav-item dropdown hover-pointer"
                >
                  <div class="nav-link" :class="{ active: currentSchedulePeriodId === period.id }">
                    <Popper
                      placement="top"
                      hover
                      offset-distance="4"
                      arrow
                      class="popper--light popper--nowrap"
                    >
                      <span @click="selectPeriod(period.id)">
                        {{ period.name }}
                      </span>
                      <template #content>
                        <div v-html="tabTooltip(period)"></div>
                      </template>
                    </Popper>

                    <template v-if="currentSchedulePeriodId === period.id">
                      <a
                        class="ml-3"
                        data-toggle="dropdown"
                        href="#"
                        role="button"
                        aria-expanded="false"
                      >
                        <i class="fa-solid fa-ellipsis-vertical"></i>
                      </a>
                      <div class="dropdown-menu" style="z-index: 999">
                        <span class="dropdown-item" @click="duplicatePeriod(period.id)">
                          <i class="fa-solid fa-copy"></i>
                          {{ duplicateLabel }}
                        </span>
                        <span
                          class="dropdown-item text-danger"
                          v-if="schedulePeriods.length > 1"
                          @click="deletePeriod(period.id)"
                        >
                          <i class="fa-solid fa-trash"></i>
                          {{ deleteLabel }}
                        </span>
                      </div>
                    </template>
                  </div>
                </li>
                <li key="add-period" class="nav-item hover-pointer">
                  <Popper
                    :content="addPeriodTooltip"
                    placement="top"
                    hover
                    offset-distance="4"
                    arrow
                    class="popper--light"
                  >
                    <span class="nav-link" @click="addPeriod()">
                      <i class="fa-light fa-plus"></i>
                    </span>
                  </Popper>
                </li>
              </ul>
            </div>
            <div>
              <div v-if="showMultiplePeriodForm" class="d-flex flex-column" style="gap: 1.5em">
                <span v-html="periodDateTitle(currentSchedulePeriod)"></span>
                <div class="d-block">
                  <WeeksSelect
                    :key="currentSchedulePeriod.id"
                    :period="currentSchedulePeriod"
                    :weeks-options="weeksOptions"
                    :adviser-id="adviser.teamMemberId"
                  />
                </div>
              </div>
              <div class="d-block">
                <div :class="['alert', 'alert-outline', 'alert-info']" role="alert">
                  <p
                    class="mb-0"
                    v-dompurify-html="
                      $t(
                        'models/conciergeAdviserSchedulePeriods.schedule.notifications.calendar_instructions'
                      )
                    "
                  ></p>
                </div>
                <Popper
                  :content="
                    $t(
                      'models/conciergeAdviserSchedulePeriods.schedule.tooltips.schedule_business_hour'
                    )
                  "
                  placement="left"
                  hover
                  offset-distance="-1"
                  arrow
                  class="popper--primary"
                >
                  <button
                    type="button"
                    class="btn btn-outline-primary btn-sm mr-2"
                    :disabled="
                      fillScheduleForm.processing &&
                      currentSchedulePeriod.availabilities?.length > 0
                    "
                    @click="fillScheduleByBusinessHours"
                  >
                    {{
                      $t('models/conciergeAdviserSchedulePeriods.schedule.actions.fill_all')
                    }}&nbsp;
                    <i v-if="fillScheduleForm.processing" class="fa fa-spinner fa-spin"></i>
                  </button>
                </Popper>
                <button
                  v-if="currentSchedulePeriod.availabilities?.length > 0"
                  type="button"
                  class="btn btn-sm"
                  :class="{
                    'btn-outline-danger': currentSchedulePeriod.availabilities?.length > 0,
                    'btn-outline-secondary btn-disabled':
                      currentSchedulePeriod.availabilities?.length === 0
                  }"
                  :disabled="
                    clearScheduleForm.processing ||
                    currentSchedulePeriod.availabilities?.length === 0
                  "
                  @click="clearSchedule()"
                >
                  {{
                    $t('models/conciergeAdviserSchedulePeriods.schedule.actions.clear_all')
                  }}&nbsp;
                  <i class="fa fa-spinner fa-spin" v-if="clearScheduleForm.processing"></i>
                </button>
              </div>
            </div>
          </div>
        </template>
      </div>
      <template v-if="isEditable" :key="'schedule-container-' + currentSchedulePeriod.id">
        <ScheduleCalendar
          :key="
            'calendar-' +
            currentSchedulePeriod.id +
            '-' +
            (currentSchedulePeriod.availabilities?.length ?? 0)
          "
          :schedule-period="currentSchedulePeriod"
          :business-hours="businessHours"
          :week-length="weekLength"
          :is-editable="isEditable"
          @update="handleEvents"
          @uneditable="uneditableAdviserNotification"
        />
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { useForm, router } from '@inertiajs/vue3'
import { wTrans } from 'laravel-vue-i18n'
import ActiveAdviserToggle from '@components/concierge/ActiveAdviserToggle.vue'
import ScheduleCalendar from '@components/concierge/ScheduleCalendar.vue'
import MondayPicker from '@components/concierge/schedule/MondayPicker.vue'
import WeeksSelect from '@components/concierge/schedule/WeeksSelect.vue'
import type {
  ConciergeAdviserScheduleAdviserData,
  ConciergeAdviserSchedulePeriodData
} from '~types/concierge-admin'
import { PropType, ref } from 'vue'

type AvailabilityPayload = {
  weekday: number
  start_time: string
  end_time: string
}

export default defineComponent({
  name: 'AdviserScheduleEditPage',
  components: {
    ScheduleCalendar,
    ActiveAdviserToggle,
    MondayPicker,
    WeeksSelect
  },
  props: {
    adviser: {
      type: Object as PropType<ConciergeAdviserScheduleAdviserData>,
      required: true
    },
    schedulePeriods: {
      type: Array as PropType<ConciergeAdviserSchedulePeriodData[]>,
      required: true
    },
    businessHours: {
      type: Array,
      required: true
    },
    weekLength: {
      type: Number,
      required: true
    },
    isEditable: {
      type: Boolean,
      required: true
    },
    scheduleCycleStartsOn: {
      type: String,
      required: true
    },
    weeksOptions: {
      type: Object as PropType<{ [key: number]: string }>,
      required: true
    },
    adviserSchedulePeriodsEnabled: {
      type: Boolean,
      required: true
    }
  },
  setup(props) {
    const addPeriodTooltip = wTrans(
      'models.conciergeAdviserSchedulePeriods.tabs.tooltips.add_period'
    )
    const duplicateLabel = wTrans('models/conciergeAdviserSchedulePeriods.tabs.menu.duplicate')
    const deleteLabel = wTrans('models/conciergeAdviserSchedulePeriods.tabs.menu.delete')
    const fillScheduleForm = useForm<Record<string, never>>({})
    const clearScheduleForm = useForm<Record<string, never>>({})
    const scheduleForm = useForm<{ availabilities: AvailabilityPayload[] }>({ availabilities: [] })
    const uneditableMessage = wTrans(
      'models/conciergeAdviserSchedulePeriods.schedule.message_when_uneditable'
    )
    const currentSchedulePeriodId = ref<number | null>(null)
    currentSchedulePeriodId.value =
      props.schedulePeriods.find(period => period.isCurrentPeriod)?.id ||
      props.schedulePeriods.find(() => true)?.id ||
      null

    return {
      currentSchedulePeriodId,
      fillScheduleForm,
      clearScheduleForm,
      scheduleForm,
      uneditableMessage,
      addPeriodTooltip,
      duplicateLabel,
      deleteLabel
    }
  },
  computed: {
    showMondayPicker(): boolean {
      return (
        this.adviserSchedulePeriodsEnabled &&
        this.schedulePeriods.length > 1 &&
        this.isEditable &&
        !!this.scheduleCycleStartsOn
      )
    },
    showMultiplePeriodForm(): boolean {
      return this.isEditable && this.adviserSchedulePeriodsEnabled
    },
    calendarInstructions(): string {
      return this.$t(
        'models/conciergeAdviserSchedulePeriods.schedule.notifications.calendar_instructions'
      )
    },
    currentSchedulePeriod(): ConciergeAdviserSchedulePeriodData {
      return (
        this.schedulePeriods.find(period => period.id === this.currentSchedulePeriodId) ||
        ({} as ConciergeAdviserSchedulePeriodData)
      )
    }
  },
  methods: {
    selectPeriod(periodId: number): void {
      this.currentSchedulePeriodId = periodId
    },
    tabTooltip(period: ConciergeAdviserSchedulePeriodData): string {
      return period.periodDateTitle
    },
    periodDateTitle(period: ConciergeAdviserSchedulePeriodData): string {
      const parts = period.periodDateTitle.split('<br>')
      if (parts.length > 1) {
        return '<span class="fw-600">' + parts[0] + '</span>' + parts.slice(1).join('<br>')
      }
      return period.periodDateTitle
    },
    uneditableAdviserNotification(): void {
      this.$toast.open({
        message: this.uneditableMessage,
        type: 'error',
        duration: 5000,
        dismissible: true
      })
    },
    fillScheduleByBusinessHours(): void {
      if (!this.isEditable) {
        this.uneditableAdviserNotification()
        return
      }

      this.fillScheduleForm.put(
        route('conciergeAdvisers.schedules.fillByDepartmentBusinessHours', [
          this.adviser.teamMemberId,
          this.currentSchedulePeriodId
        ]),
        { preserveScroll: true }
      )
    },
    clearSchedule(): void {
      if (!this.isEditable) {
        this.uneditableAdviserNotification()
        return
      }

      this.clearScheduleForm.patch(
        route('conciergeAdvisers.schedules.clear', [
          this.adviser.teamMemberId,
          this.currentSchedulePeriodId
        ]),
        {
          preserveScroll: true
        }
      )
    },
    handleEvents(availabilities: AvailabilityPayload[]): void {
      this.scheduleForm.availabilities = availabilities

      this.scheduleForm.put(
        route('conciergeAdvisers.schedules.update', [
          (this.adviser as any).teamMemberId,
          this.currentSchedulePeriodId
        ]),
        {
          preserveScroll: true
        }
      )
    },
    addPeriod(): void {
      router.post(
        route('conciergeAdvisers.schedules.period.add', [this.adviser.teamMemberId]),
        {},
        {
          preserveScroll: true,
          onFlash: ({ schedulePeriodId }) => {
            this.currentSchedulePeriodId = schedulePeriodId as number
          }
        }
      )
    },
    duplicatePeriod(periodId: number): void {
      router.post(
        route('conciergeAdvisers.schedules.period.duplicate', [
          this.adviser.teamMemberId,
          periodId
        ]),
        {},
        {
          preserveScroll: true,
          onFlash: ({ schedulePeriodId }) => {
            this.currentSchedulePeriodId = schedulePeriodId as number
          }
        }
      )
    },
    deletePeriod(periodId: number): void {
      router.delete(
        route('conciergeAdvisers.schedules.period.delete', [this.adviser.teamMemberId, periodId]),
        {
          preserveScroll: true,
          onFlash: ({ schedulePeriodId }) => {
            this.currentSchedulePeriodId = schedulePeriodId as number
          }
        }
      )
    }
  }
})
</script>
<style>
.popper--nowrap > .popper {
  white-space: nowrap;
  overflow: hidden;
}
</style>
